import noop from 'utils/noop';
import { useTranslation } from 'modules/queryBuilder/hooks/useTranslation';
import { InputTextProps } from 'ui/inputs';
import { SelectValueType } from 'ui/selects';

import { StyledInput } from './ConditionInput.styles';
import { ConditionSingleValueSelectProps } from '../types';
import { useConditionInputWithoutOptionsState } from './useConditionInputWithoutOptionsState';
import { ENTER_KEY } from 'constants/keyCodes';

export const DATA_TEST = {
  inputWithoutOptions: 'condition-single-value-input-withoutOptions',
};

export type ConditionInputWithoutOptionsProps<TValue> = ConditionSingleValueSelectProps<TValue> &
  Pick<
    InputTextProps,
    'type' | 'minValue' | 'maxValue' | 'Prefix' | 'width' | 'autoFocus' | 'step'
  > &
  Partial<{
    setIsEditing: (editingMode: boolean) => void;
  }>;

export const ConditionInputWithoutOptions = <TValue extends SelectValueType = string>({
  onChange = noop,
  isDisabled = false,
  isReadOnly = false,
  type,
  value,
  setIsEditing = noop,
  placeholder,
  maxValue,
  minValue,
  ...props
}: ConditionInputWithoutOptionsProps<TValue>) => {
  const { t } = useTranslation();
  const { inputValue, setInputValue, onUpdateValue } = useConditionInputWithoutOptionsState({
    value,
    onChange,
    type,
    setIsEditing,
    maxValue,
    minValue,
  });

  return (
    <StyledInput
      {...props}
      type={type}
      onKeyDown={({ keyCode }) => {
        if (keyCode === ENTER_KEY) onUpdateValue();
      }}
      data-test={DATA_TEST.inputWithoutOptions}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      value={inputValue}
      placeholder={placeholder ?? t('storybook.placeholders.input')}
      onChange={setInputValue}
      onBlur={onUpdateValue}
      maxValue={maxValue}
      minValue={minValue}
    />
  );
};
